import { baseApi } from './baseApi'

import {
  PaperQueueList,
  PaperQueueView,
  PatientReturnedMailDto,
  StatementNotMailedDto,
  StatementPrintRunDetailsDto,
  StatementPrintRunItemDto,
  StatementPrintRunsList,
} from 'store/types'

type PaperQueueRequest = {
  view: PaperQueueView
  page: number
  size: number
  patientFirstName?: string
  patientLastName?: string
}

type ConfirmPrintRunRequest = {
  runId: string
  notMailed: (StatementNotMailedDto & { itemId: string })[]
}

const BASE_URL = '/lab/patient-statements/paper'

// Anything that moves a statement through the queue also moves the badge and
// the patient's delivery history.
const QUEUE_TAGS = ['PaperStatements', 'PatientStatements', 'OrderCounts'] as const

export const paperStatementsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaperQueue: builder.query<PaperQueueList, PaperQueueRequest>({
      query: (params) => ({ url: `${BASE_URL}/queue`, params }),
      providesTags: ['PaperStatements'],
    }),
    // Re-read when the patient is edited, since a new address clears it.
    getPatientReturnedMail: builder.query<PatientReturnedMailDto, number>({
      query: (patientId) => ({ url: `${BASE_URL}/patients/${patientId}/returned-mail` }),
      providesTags: ['PaperStatements', 'Patient'],
    }),
    releasePaperHold: builder.mutation<void, { cycleId: string }>({
      query: ({ cycleId }) => ({ url: `${BASE_URL}/queue/${cycleId}/release-hold`, method: 'POST' }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
    printPaperStatements: builder.mutation<StatementPrintRunDetailsDto, { cycleIds: string[] }>({
      query: (body) => ({ url: `${BASE_URL}/runs`, method: 'POST', body }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
    getStatementPrintRuns: builder.query<StatementPrintRunsList, { page: number; size: number }>({
      query: (params) => ({ url: `${BASE_URL}/runs`, params }),
      providesTags: ['PaperStatements'],
    }),
    getStatementPrintRun: builder.query<StatementPrintRunDetailsDto, string>({
      query: (runId) => ({ url: `${BASE_URL}/runs/${runId}` }),
      providesTags: ['PaperStatements'],
    }),
    getStatementPrintRunDownload: builder.query<{ url: string }, string>({
      query: (runId) => ({ url: `${BASE_URL}/runs/${runId}/download` }),
    }),
    confirmStatementPrintRun: builder.mutation<StatementPrintRunDetailsDto, ConfirmPrintRunRequest>({
      query: ({ runId, ...body }) => ({ url: `${BASE_URL}/runs/${runId}/confirm`, method: 'POST', body }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
    cancelStatementPrintRun: builder.mutation<StatementPrintRunDetailsDto, string>({
      query: (runId) => ({ url: `${BASE_URL}/runs/${runId}/cancel`, method: 'POST' }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
    markStatementNotMailed: builder.mutation<
      StatementPrintRunItemDto,
      StatementNotMailedDto & { itemId: string }
    >({
      query: ({ itemId, ...body }) => ({ url: `${BASE_URL}/items/${itemId}/not-mailed`, method: 'POST', body }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
    markStatementReturned: builder.mutation<StatementPrintRunItemDto, string>({
      query: (itemId) => ({ url: `${BASE_URL}/items/${itemId}/returned`, method: 'POST' }),
      invalidatesTags: [...QUEUE_TAGS],
    }),
  }),
})

export const {
  useGetPaperQueueQuery,
  useLazyGetPaperQueueQuery,
  useGetPatientReturnedMailQuery,
  useReleasePaperHoldMutation,
  usePrintPaperStatementsMutation,
  useGetStatementPrintRunsQuery,
  useGetStatementPrintRunQuery,
  useLazyGetStatementPrintRunDownloadQuery,
  useConfirmStatementPrintRunMutation,
  useCancelStatementPrintRunMutation,
  useMarkStatementNotMailedMutation,
  useMarkStatementReturnedMutation,
} = paperStatementsApi
